import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Table,
  Button,
  Alert,
  Form,
  Badge,
  Image,
  Card,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import { blogsAPI } from '../services/api';

interface BlogDocument {
  id: number;
  file_path: string;
  file_type: 'image' | 'document';
}

interface Blog {
  id: number; 
  title: string;
  slug: string;
  category?: string;
  status: 'draft' | 'published';
  featured_image?: string;
  created_at: string;
  published_at?: string;
  documents: BlogDocument[];
}

const Blogs: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    loadBlogs();
  }, [status, category]);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const response = await blogsAPI.getAll({
        status,
        category
      });
      setBlogs(response.data);
      setError('');
    } catch {
      setError('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      await blogsAPI.delete(String(id));
      loadBlogs();
    } catch {
      setError('Failed to delete blog');
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const getBlogImage = (blog: Blog) => {
    if (blog.featured_image) return blog.featured_image;

    const imageDoc = blog.documents?.find(
      doc => doc.file_type === 'image'
    );

    return imageDoc?.file_path || '/placeholder-image.png';
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ 
            color: '#00579a', 
            fontWeight: 600,
            fontSize: '28px',
            margin: 0
          }}>
            Blogs Management
          </h1>
          <Link 
            to="/blogs/new" 
            className="btn"
            style={{
              backgroundColor: '#00579a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 20px',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
              transition: 'all 0.3s ease',
              textDecoration: 'none'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#004a85'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#00579a'}
          >
            + Add New Blog
          </Link>
        </div>

        {error && (
          <Alert 
            variant="danger" 
            dismissible 
            onClose={() => setError('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
              marginBottom: '1.5rem'
            }}
          >
            {error}
          </Alert>
        )}

        <Card style={{ 
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          marginBottom: '1.5rem'
        }}>
          <Card.Body style={{ padding: '20px' }}>
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{ 
                    fontWeight: 500, 
                    color: '#262626',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}>
                    Status
                  </Form.Label>
                  <Form.Select
                    value={status}
                    onChange={e => setStatus(e.target.value)}
                    style={{
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                      padding: '8px 12px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">All</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label style={{ 
                    fontWeight: 500, 
                    color: '#262626',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}>
                    Category
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Filter by category"
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    style={{
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                      padding: '8px 12px',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card style={{ 
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          overflow: 'hidden'
        }}>
          <Card.Body style={{ padding: 0 }}>
            {loading ? (
              <div className="text-center" style={{ padding: '60px 0' }}>
                <Spinner 
                  animation="border" 
                  style={{ color: '#00579a', width: '40px', height: '40px' }}
                />
                <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading blogs...</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table 
                  hover 
                  responsive 
                  style={{ 
                    margin: 0,
                    borderCollapse: 'separate',
                    borderSpacing: 0
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#fafafa' }}>
                      <th style={{ ...headerStyle, width: '90px' }}>Image</th>
                      <th style={headerStyle}>Title</th>
                      <th style={headerStyle}>Category</th>
                      <th style={headerStyle}>Status</th>
                      <th style={headerStyle}>Files</th>
                      <th style={headerStyle}>Created</th>
                      <th style={headerStyle}>Published</th>
                      <th style={{ ...headerStyle, width: '160px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center" style={{ 
                          padding: '60px 20px',
                          color: '#8c8c8c',
                          fontSize: '15px'
                        }}>
                          No blog posts found. Click "Add New Blog" to create one.
                        </td>
                      </tr>
                    ) : (
                      blogs.map(blog => (
                        <tr 
                          key={blog.id}
                          style={{
                            transition: 'background-color 0.2s ease'
                          }}
                        >
                          <td style={cellStyle} className="text-center">
                            <Image
                              src={getBlogImage(blog)}
                              alt={blog.title}
                              style={{
                                width: '70px',
                                height: '70px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: '1px solid #f0f0f0'
                              }}
                            />
                          </td>

                          <td style={cellStyle}>
                            <div style={{ fontWeight: 500, color: '#262626', marginBottom: '4px' }}>
                              {blog.title}
                            </div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                              /{blog.slug}
                            </div>
                          </td>

                          <td style={cellStyle}>
                            {blog.category ? (
                              <Badge 
                                style={{
                                  backgroundColor: '#e6f4ff',
                                  color: '#ffffffff',
                                  fontWeight: 500,
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}
                              >
                                {blog.category}
                              </Badge>
                            ) : (
                              <span style={{ color: '#8c8c8c' }}>-</span>
                            )}
                          </td>

                          <td style={cellStyle}>
                            {blog.status === 'published' ? (
                              <Badge 
                                style={{
                                  backgroundColor: '#f6ffed',
                                  color: '#ffffffff',
                                  fontWeight: 500,
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}
                              >
                                Published
                              </Badge>
                            ) : (
                              <Badge 
                                style={{
                                  backgroundColor: '#f0f0f0',
                                  color: '#000000ff',
                                  fontWeight: 500,
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}
                              >
                                Draft
                              </Badge>
                            )}
                          </td>

                          <td style={cellStyle}>
                            <Badge 
                              style={{
                                backgroundColor: '#f0f0f0',
                                color: '#ffffffff',
                                fontWeight: 500,
                                padding: '4px 10px',
                                borderRadius: '4px'
                              }}
                            >
                              {blog.documents?.length || 0} files
                            </Badge>
                          </td>

                          <td style={cellStyle}>{formatDate(blog.created_at)}</td>
                          <td style={cellStyle}>{formatDate(blog.published_at)}</td>

                          <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                            <Link
                              to={`/blogs/edit/${blog.id}`}
                              className="btn btn-sm me-2"
                              style={{
                                backgroundColor: 'white',
                                color: '#00579a',
                                border: '1px solid #00579a',
                                borderRadius: '4px',
                                padding: '4px 12px',
                                fontSize: '13px',
                                fontWeight: 500,
                                transition: 'all 0.2s ease',
                                textDecoration: 'none'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#e6f4ff';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                              }}
                            >
                              Edit
                            </Link>
                            <Button
                              size="sm"
                              onClick={() => handleDelete(blog.id)}
                              style={{
                                backgroundColor: 'white',
                                color: '#ff4d4f',
                                border: '1px solid #ff4d4f',
                                borderRadius: '4px',
                                padding: '4px 12px',
                                fontSize: '13px',
                                fontWeight: 500,
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#fff1f0';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                              }}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const headerStyle: React.CSSProperties = {
  padding: '16px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#000000ff',
  borderBottom: '1px solid #f0f0f0',
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap'
};

const cellStyle: React.CSSProperties = {
  padding: '16px',
  fontSize: '14px',
  color: '#000000ff',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle'
};

export default Blogs;